/**
 * Performs a parcel search using an ArcGIS Feature Service or coordinate input.
 *
 * Depending on the provided `SearchJsonType`, this function either:
 * - Returns a coordinate-based result (when `searchType === "coordinates"`), or
 * - Executes a layer query (`/query`) against a feature service, applying field values
 *   from the provided `values` object to a `WHERE` clause template.
 *
 * It also decodes any coded value domains using `getDomainGuide` and `applyDomains`
 * for more readable attribute data.
 */

import { SearchJsonType, SearchResult, HttpProxyConfig } from "../types/config";
import { buildWhere } from "./BuildWhere";
import { proxify } from "./proxy";
import { getDomainGuide, applyDomains } from "./domains";

async function fetchWithRetry(
    input: RequestInfo,
    init?: RequestInit,
    retries = 2,
    delayMs = 800,
): Promise<Response> {
    for (let attempt = 0; attempt <= retries; attempt++) {
        try {
            const res = await fetch(input, init);
            if (res.ok || attempt === retries) return res;
            console.warn(`Fetch attempt ${attempt + 1} returned ${res.status}, retrying...`);
        } catch (err) {
            if (attempt === retries) throw err;
            console.warn(`Fetch attempt ${attempt + 1} failed, retrying...`, err);
        }
        await new Promise((r) => setTimeout(r, delayMs * (attempt + 1)));
    }
    return fetch(input, init);
}

function isCoords(type: SearchJsonType): boolean {
    return (type as any)?.searchType === "coordinates";
}
function hasQueryLayer(type: SearchJsonType): type is SearchJsonType & { queryLayer: string } {
    return typeof (type as any)?.queryLayer === "string" && (type as any).queryLayer.length > 0;
}

function centerOfGeometry(geom: any): { latitude: number; longitude: number } {
    if (geom?.x != null && geom?.y != null) return { longitude: geom.x, latitude: geom.y };
    if (Array.isArray(geom?.points) && geom.points.length) {
        const [x, y] = geom.points[0];
        return { longitude: x, latitude: y };
    }
    const coords: number[][] =
        (geom?.paths?.flat?.(1) as number[][]) ||
        (geom?.rings?.flat?.(1) as number[][]) ||
        [];
    if (coords.length) {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const [x, y] of coords) {
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
        }
        return { longitude: (minX + maxX) / 2, latitude: (minY + maxY) / 2 };
    }
    return { latitude: 0, longitude: 0 };
}

export async function searchParcels(
    type: SearchJsonType,
    values: Record<string, string>,
    proxy?: HttpProxyConfig
): Promise<SearchResult[]> {
    if (isCoords(type)) {
        const latKey = (type as any).latField ?? "latitude";
        const lonKey = (type as any).longField ?? "longitude";
        const lat = parseFloat(values[latKey] ?? "");
        const lon = parseFloat(values[lonKey] ?? "");
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return [];
        return [
            {
                id: "coords-0",
                label: `${lat.toFixed(5)}, ${lon.toFixed(5)}`,
                center: { latitude: lat, longitude: lon },
                attrs: { [latKey]: lat, [lonKey]: lon },
            },
        ];
    }

    if (!hasQueryLayer(type)) {
        throw new Error("Unsupported search type: missing queryLayer");
    }

    const where = buildWhere(type.query.where, values);
    const base = type.queryLayer.replace(/\/$/, "");
    const url = proxify(`${base}/query`, proxy);

    const params = new URLSearchParams({
        f: "json",
        where,
        outFields: (type.query.outFields || ["*"]).join(","),
        orderByFields: (type.query.orderByFields || []).join(","),
        returnGeometry: String(!!type.query.returnGeometry),
        outSR: "4326",
    });

    const res = await fetch(`${url}?${params.toString()}`);
    if (!res.ok) throw new Error(`Search failed: ${res.status} ${res.statusText}`);
    const j = await res.json();
    const feats: any[] = j.features || [];

    // decode coded value domains so RES/HS → Residence/House, etc.
    const domainGuide = await getDomainGuide(base);

    // If results have no geometry, try to fetch geometry from related parcel layer
    const needsGeometry = feats.length > 0 && !feats[0].geometry;
    let geometryMap: Map<string, any> = new Map();

    if (needsGeometry) {
        // Try to fetch geometry from layer 0 (Parcels) using PARCEL_ID
        let parcelIds = feats.map(f => f.attributes?.PARCEL_ID || f.attributes?.NEW_PARCEL_NUMBER).filter(Boolean);

        if (parcelIds.length > 0) {
            try {
                const parcelBase = base.replace(/\/\d+$/, "/0");
                const parcelUrl = proxify(`${parcelBase}/query`, proxy);

                // Check condo layer (layer 1) for ALL parcel IDs to handle condo units
                // Condos don't have geometry in layer 0, they reference a parent parcel
                const condoBase = base.replace(/\/\d+$/, "/1");
                const condoUrl = proxify(`${condoBase}/query`, proxy);

                // Query condo layer in batches
                const CONDO_BATCH_SIZE = 100;
                const condoBatches: string[][] = [];
                for (let i = 0; i < parcelIds.length; i += CONDO_BATCH_SIZE) {
                    condoBatches.push(parcelIds.slice(i, i + CONDO_BATCH_SIZE));
                }

                const condoBatchPromises = condoBatches.map(async (batch) => {
                    const whereClause = batch.map(id => `PARCEL_ID='${String(id).replace(/'/g, "''")}'`).join(" OR ");
                    const params = new URLSearchParams({
                        f: "json",
                        where: whereClause,
                        outFields: "PARCEL_ID,PARENT_PAR,CONDO_Prcl",
                        returnGeometry: "false",
                    });

                    const res = await fetchWithRetry(condoUrl, {
                        method: "POST",
                        headers: { "Content-Type": "application/x-www-form-urlencoded" },
                        body: params.toString(),
                    });

                    if (res.ok) {
                        const json = await res.json();
                        return json.features || [];
                    }
                    return [];
                });

                const condoResults = await Promise.all(condoBatchPromises);
                const condoFeatures = condoResults.flat();

                // Map condo PARCEL_ID to parent parcel ID
                const condoToParentMap = new Map<string, string>();
                condoFeatures.forEach((feat: any) => {
                    const condoId = feat.attributes?.PARCEL_ID;
                    const parentId = feat.attributes?.PARENT_PAR || feat.attributes?.CONDO_Prcl;
                    if (condoId && parentId && parentId.trim()) {
                        condoToParentMap.set(condoId, parentId.trim());
                    }
                });

                // Replace condo IDs with their parent parcel IDs for geometry lookup
                parcelIds = parcelIds.map(id => condoToParentMap.get(id) || id);

                // Store the mapping for later use when assigning geometries
                (geometryMap as any).__condoToParent = condoToParentMap;

                // Batch queries to avoid "SQL query nested too deeply" error
                // ArcGIS Server has limits on query complexity - split into chunks of 100
                const BATCH_SIZE = 100;
                const batches: string[][] = [];
                for (let i = 0; i < parcelIds.length; i += BATCH_SIZE) {
                    batches.push(parcelIds.slice(i, i + BATCH_SIZE));
                }

                // Process all batches in parallel
                const batchPromises = batches.map(async (batch) => {
                    // Escape single quotes in parcel IDs to prevent SQL injection and query errors
                    const whereClause = batch.map(id => `PARCEL_ID='${String(id).replace(/'/g, "''")}'`).join(" OR ");

                    const parcelParams = new URLSearchParams({
                        f: "json",
                        where: whereClause,
                        outFields: "PARCEL_ID,OBJECTID",
                        returnGeometry: "true",
                        outSR: "4326",
                    });

                    const parcelRes = await fetchWithRetry(parcelUrl, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/x-www-form-urlencoded",
                        },
                        body: parcelParams.toString(),
                    });

                    if (!parcelRes.ok) {
                        const errorText = await parcelRes.text();
                        console.error(`Batch query failed: ${parcelRes.status} ${parcelRes.statusText}`, errorText);
                        return [];
                    }

                    const parcelJson = await parcelRes.json();
                    return parcelJson.features || [];
                });

                const batchResults = await Promise.all(batchPromises);
                const allFeatures = batchResults.flat();

                allFeatures.forEach((pFeat: any) => {
                    const pid = pFeat.attributes?.PARCEL_ID;
                    if (pid && pFeat.geometry) {
                        geometryMap.set(pid, pFeat.geometry);
                    }
                });

                // For condos, also map the condo unit IDs to the parent geometry
                const storedCondoMap = (geometryMap as any).__condoToParent;
                if (storedCondoMap && storedCondoMap.size > 0) {
                    storedCondoMap.forEach((parentId: string, condoId: string) => {
                        const parentGeometry = geometryMap.get(parentId);
                        if (parentGeometry) {
                            geometryMap.set(condoId, parentGeometry);
                        }
                    });
                }
            } catch (err) {
                console.error("Failed to fetch parcel geometries:", err);
            }
        }
    }

    const geometryFetchFailed = needsGeometry && geometryMap.size === 0;

    return feats.map((feat, i) => {
        const a = { ...(feat.attributes || {}) };
        applyDomains(a, domainGuide);

        let g = feat.geometry || null;
        // If no geometry, try to get it from the geometry map
        if (!g && geometryMap.size > 0) {
            const pid = a.PARCEL_ID || a.NEW_PARCEL_NUMBER;
            if (pid) {
                g = geometryMap.get(pid) || null;
                if (!g) {
                    console.warn(`No geometry found in map for parcel ${pid}`);
                }
            }
        }

        const center = g ? centerOfGeometry(g) : { latitude: 0, longitude: 0 };
        if (!g) {
            console.warn(`Feature ${i} has no geometry, defaulting to (0,0)`, { parcelId: a.PARCEL_ID, attributes: a });
        }

        return {
            id: String(a.OBJECTID ?? i),
            label: [a.Placename, a.FullAddr, a.PPIN, a.PARCEL_ID].filter(Boolean).join(" — ") || `Result ${i + 1}`,
            center,
            raw: { ...feat, geometry: g }, // Include fetched geometry
            attrs: a,
            geometryFetchFailed: !g && geometryFetchFailed,
        } as SearchResult;
    });
}
